// Ponto único de entrada da telemetria de UX.
// Substitui a chamada antiga de initFirebaseAnalytics() em src/App.js.
// Cada coletor é robusto a falhas: nenhum erro de telemetria pode derrubar o app.

import { initFirebaseAnalytics, initAppCheck } from './firebase';
import { logUxEvent, setUxUserFromStorage } from './uxLogger';
import { initSession } from './session';
import { initQueue } from './queue';
import { initWebVitals } from './webVitals';
import { initFriction } from './friction';
import { initErrors } from './errors';
import { initScrollDepth } from './scrollDepth';

let initialized = false;

// ─── Diagnostico: estado do localStorage no boot ──────────────────────
// Registra UMA VEZ por sessao um snapshot booleano do que esta em LS,
// para mensurar quantos pacientes chegam ao app com qual subset de
// identidade preservado. Permite descobrir se a perda eh:
//   - patientId apenas (cache buster bem-comportado)
//   - tudo (Safari ITP / modo privado)
//   - aleatorio (corrupcao)
//
// Sem PII: apenas booleans + counts. Tambem registra os atributos de
// ambiente que correlacionam com perda de LS (browser, os, modo).
const STORAGE_STATE_FLAG = 'ux_storage_state_logged';
const logStorageStateOnce = () => {
  try {
    if (typeof window === 'undefined') return;
    if (sessionStorage.getItem(STORAGE_STATE_FLAG) === '1') return;

    const ls = (k) => { try { return localStorage.getItem(k); } catch { return null; } };
    const ss = (k) => { try { return sessionStorage.getItem(k); } catch { return null; } };

    let lsCount = 0;
    try { lsCount = localStorage.length || 0; } catch {}

    let cookieCount = 0;
    try { cookieCount = (document.cookie || '').split(';').filter((s) => s.trim()).length; } catch {}

    const patientId = ls('patientId');
    const sessionStartedAt = parseInt(ss('ux_session_started_at') || '0', 10) || 0;

    sessionStorage.setItem(STORAGE_STATE_FLAG, '1');
    logUxEvent('storage_state_boot', {
      // identidade
      has_patient_id: !!patientId,
      has_full_name: !!(ls('fullName') || ls('patientName')),
      has_email: !!ls('patientEmail'),
      has_cpf: !!ls('patientCpf'),
      has_phone: !!ls('patientPhone'),
      // estado do chat
      has_active_channel: !!ls('activeChannelId'),
      has_payment_confirmed: ls('paymentConfirmed') === '1',
      has_finalized_at: !!ls('finalizedAt'),
      has_chat_started_at: !!ls('chatStartedAt'),
      has_anamnese_prontuario: !!ls('anamneseProntuario'),
      // tamanho geral
      ls_size: lsCount,
      cookie_count: cookieCount,
      // session
      session_age_min: sessionStartedAt ? Math.round((Date.now() - sessionStartedAt) / 60000) : 0,
      // path do boot
      path: window.location.pathname,
      // Página veio da cópia de emergência do service worker (sw.js).
      shell_fallback: !!window.__CM_SHELL_FALLBACK__,
    });
  } catch {}
};

// ─── Diagnostico: contagem de reloads do chat (mesma session) ─────────
// Cada vez que /consulta/chat ou /consulta/chat/pago carrega na mesma
// session, incrementamos contador. Logamos quando passa de 3 (sintoma de
// paciente recarregando demais por instabilidade da conexao Stream).
const CHAT_RELOAD_KEY = 'ux_chat_reload_count';
const trackChatReload = () => {
  try {
    if (typeof window === 'undefined') return;
    const path = window.location.pathname || '';
    if (!/^\/consulta\/chat(\/.*)?$/.test(path)) return;
    let count = parseInt(sessionStorage.getItem(CHAT_RELOAD_KEY) || '0', 10) || 0;
    count += 1;
    sessionStorage.setItem(CHAT_RELOAD_KEY, String(count));
    if (count === 1) return; // 1o load nao eh reload
    if (count <= 10 || count % 5 === 0) {
      // Loga em 2..10 e depois 15, 20, 25... (evita spam pra quem fica recarregando)
      logUxEvent('chat_reload_within_session', {
        count,
        path,
        session_minutes: parseInt(sessionStorage.getItem('ux_session_started_at') || '0', 10)
          ? Math.round((Date.now() - parseInt(sessionStorage.getItem('ux_session_started_at') || '0', 10)) / 60000)
          : 0,
      });
    }
  } catch {}
};

export const initTelemetry = () => {
  if (initialized) return;
  initialized = true;

  // Ordem importa pouco, mas Errors antes de tudo cobre falhas dos próximos init.
  try { initErrors(); } catch {}
  try { initSession(); } catch {}
  try { initQueue(); } catch {}
  try { initFirebaseAnalytics(); } catch {}
  // Onda 1A hardening 2026-06: App Check (no-op se REACT_APP_APPCHECK_RECAPTCHA_KEY vazia).
  try { initAppCheck(); } catch {}
  try { setUxUserFromStorage(); } catch {}
  try { initWebVitals(); } catch {}
  try { initFriction(); } catch {}
  try { initScrollDepth(); } catch {}
  // Diagnosticos pos-init (precisam de session pronta).
  try { logStorageStateOnce(); } catch {}
  try { trackChatReload(); } catch {}
};

// Reexports convenientes para os pontos de uso existentes não precisarem mudar.
export { logUxEvent, setUxUserFromStorage } from './uxLogger';
